import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Row, Col, Table, CardBody } from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { useSelector } from 'react-redux'

const FocCreateDi = () => {
    const token = localStorage.getItem('token')
    const userDetails = useSelector(store => store.user)
    const canCreateDI = userDetails?.role === 'commercial_executive' || userDetails?.role === 'admin' || userDetails?.role === 'super_admin'
    const { soid } = useParams()
    const navigate = useNavigate()

    const [soData, setSoData] = useState({})
    const [selectedQuantities, setSelectedQuantities] = useState({})
    const [includedLines, setIncludedLines] = useState({})
    const [loadingSo, setLoadingSo] = useState(false)
    const [submitting, setSubmitting] = useState(false)


    const getLineId = (line, idx) => line?._id ?? `line-${idx}`
    const getModelName = line =>
        line?.model?.modelName ??
        line?.modelName ??
        line?.name ??
        (line?.model?._id ? line.model._id : line?.model) ??
        '-'
    const getPartCode = line =>
        line?.model?.partCode ?? line?.partCode ?? line?.part_code ?? '-'

    // We assume FOC SO might have diQuantity similar to regular SO
    const getAvailableQty = line => {
        const q = Number(line?.quantity || 0)
        const diq = Number(line?.diQuantity || 0)
        return Math.max(0, q - diq)
    }

    const getDisplayLineItems = so => {
        return Array.isArray(so?.lineItems) ? so.lineItems : []
    }

    useEffect(() => {
        const fetchSO = async () => {
            setLoadingSo(true)
            try {
                const res = await fetch(`${API_URL}foc/sales-order/${soid}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const result = await res.json()
                const data = result?.data || {}
                setSoData(data)

                const itemsForInit = getDisplayLineItems(data)
                const initialQuantities = {}
                const initialIncluded = {}
                if (Array.isArray(itemsForInit)) {
                    itemsForInit.forEach((li, idx) => {
                        const lineId = getLineId(li, idx)
                        // If diQuantity exists, use it. Otherwise default to full quantity.
                        const avail = getAvailableQty(li)
                        initialQuantities[lineId] = avail
                        initialIncluded[lineId] = avail > 0
                    })
                }
                setSelectedQuantities(initialQuantities)
                setIncludedLines(initialIncluded)
            } catch (e) {
                console.error('Error fetching sales order details', e)
                alert('Error fetching sales order details')
            } finally {
                setLoadingSo(false)
            }
        }


        if (soid) fetchSO()
    }, [token, soid])

    const downloadFile = async filePath => {
        if (!filePath) {
            alert('File not available')
            return
        }
        try {
            const url = `${API_URL}file/serve`
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ filePath })
            })
            if (!response.ok) {
                alert('Error downloading file')
                return
            }
            const blob = await response.blob()
            const downloadUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = filePath.split('/').pop() || 'download'
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(downloadUrl)
        } catch (e) {
            alert('Error downloading file')
        }
    }

    const handleQuantityChange = (lineId, value, available) => {
        const numeric = Number(value || 0)
        // If available is 0 (meaning maybe not tracked?), max is quantity
        // If tracked, max is available
        // Safety check: if available is 0 but we want to allow >0?
        // Let's assume strict checking if diQuantity is present.
        const max = available
        if (numeric > max) {
            alert(`Quantity cannot exceed ${max}`)
            return
        }
        setSelectedQuantities(prev => ({ ...prev, [lineId]: numeric }))
    }

    const handleToggleInclude = lineId => {
        setIncludedLines(prev => ({ ...prev, [lineId]: !prev[lineId] }))
    }

    const displayLineItems = getDisplayLineItems(soData)

    const handleCreateDI = async () => {
        const itemsArrForDisplay = Array.isArray(displayLineItems) ? displayLineItems : []

        // Prepare DI items and include lineId for matching
        const diItems = itemsArrForDisplay
            .map((li, idx) => {
                const lineId = getLineId(li, idx)
                const qty = Number(selectedQuantities[lineId] ?? 0)

                return {
                    lineId, // Add lineId for matching
                    model: li?.model?._id ?? li?.model,
                    quantity: qty,
                    // Additional fields if supported
                }
            })
            // Filter by inclusion checkbox AND quantity > 0
            .filter((item, idx) => {
                const lineId = getLineId(itemsArrForDisplay[idx], idx)
                return includedLines[lineId] && item.quantity > 0
            })

        if (diItems.length === 0) {
            alert('Please include at least one line item with quantity > 0')
            return
        }

        setSubmitting(true)
        try {
            // Update Sales Order's diQuantity first
            const actualSoLineItems = Array.isArray(soData?.lineItems) ? soData.lineItems : []

            const updatedSoLineItems = actualSoLineItems.map(orig => {
                const origLineId = getLineId(orig, actualSoLineItems.indexOf(orig))
                // Find matching DI item
                const matched = diItems.find(d => d.lineId === origLineId)

                if (!matched) return orig

                const addedQty = Number(matched.quantity || 0)
                const currentDiQty = Number(orig.diQuantity || 0)
                const totalQty = Number(orig.quantity || 0)

                // Calculate new diQuantity, capped at totalQty
                const newDiQty = Math.min(currentDiQty + addedQty, totalQty)

                return {
                    ...orig,
                    diQuantity: newDiQty
                }
            })

            // PUT request to update SO
            const soUpdateRes = await fetch(`${API_URL}foc/sales-order/${soid}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    lineItems: updatedSoLineItems
                    // We only send lineItems to update quantities
                })
            })
            const soUpdateResult = await soUpdateRes.json()

            if (soUpdateResult.error) {
                alert(soUpdateResult.message || 'Error updating Sales Order quantities')
                setSubmitting(false)
                return
            }

            // Create DI
            const diPayload = {
                salesOrderNumber: soData?.salesOrderNumber,
                lineItems: diItems.map(({ lineId, ...rest }) => rest) // Remove lineId from payload
            }

            const diRes = await fetch(`${API_URL}foc/delivery-instruction`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(diPayload)
            })
            const diResult = await diRes.json()

            if (diResult.error) {
                alert(diResult?.message || 'Error creating Delivery Instruction')
                setSubmitting(false)
                return
            }

            // Update local state is optional since we navigate away, but good for completeness
            setSoData(prev => ({ ...prev, lineItems: updatedSoLineItems }))

            alert('FOC Delivery Instruction created successfully')
            navigate('/foc_invoice_journey_start')
        } catch (e) {
            console.error('Error:', e)
            alert('Network error while creating DI')
        } finally {
            setSubmitting(false)
        }
    }

    const formatDate = date =>
        date
            ? new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
            : '-'

    return (
        <CardBody className='p-4'>
            {/* Header Details */}
            <Row>
                <Col md={4}>
                    <label className='form-label'><strong>Sales Order Number</strong></label>
                    <p>{soData?.salesOrderNumber || '-'}</p>
                </Col>
                <Col md={4}>
                    <label className='form-label'><strong>Client</strong></label>
                    <p>{soData?.lead?.client?.companyName || soData?.client?.companyName || '-'}</p>
                </Col>
                <Col md={4}>
                    <label className='form-label'><strong>Date of SO</strong></label>
                    <p>{formatDate(soData?.createdAt)}</p>
                </Col>
                {/* Add more fields as needed */}
            </Row>

            {/* Line Items Table */}
            <div className='avoid-page-break mt-3'>
                <Table className='so-table' responsive>
                    <thead>
                        <tr>
                            {canCreateDI && <th>Include</th>}
                            <th>Name</th>
                            <th>Part Code</th>
                            <th>UOM</th>
                            <th>QTY</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(displayLineItems) && displayLineItems.length > 0 ? (
                            displayLineItems.map((data, index) => {
                                const lineId = getLineId(data, index)
                                const available = getAvailableQty(data)
                                const currentSelected = Number(selectedQuantities[lineId] ?? 0)
                                const included = !!includedLines[lineId]
                                // Generate dropdown options from 0 to available
                                const qtyOptions = available >= 0 ? Array.from({ length: available + 1 }, (_, i) => i) : [0]

                                return (
                                    <tr key={lineId}>
                                        {canCreateDI && (
                                            <td className='text-center'>
                                                <input
                                                    type='checkbox'
                                                    checked={included}
                                                    onChange={() => handleToggleInclude(lineId)}
                                                    disabled={!canCreateDI}
                                                />
                                            </td>
                                        )}
                                        <td>{getModelName(data)}</td>
                                        <td>{getPartCode(data)}</td>
                                        <td>{data?.model?.uom ?? data?.uom ?? '-'}</td>
                                        <td>
                                            {canCreateDI ? (
                                                available >= 0 ? (
                                                    <>
                                                        <select
                                                            className='form-select'
                                                            value={currentSelected}
                                                            onChange={e => handleQuantityChange(lineId, e.target.value, available)}
                                                            disabled={!included || !canCreateDI}
                                                        >
                                                            {qtyOptions.map(q => (
                                                                <option key={q} value={q}>{q}</option>
                                                            ))}
                                                        </select>
                                                        <div className='small text-muted mt-1'>
                                                            Allocated: {Number(data?.diQuantity ?? 0)} / {Number(data?.quantity ?? 0)} | Pending: {available}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className='text-muted'>0</span>
                                                )
                                            ) : (
                                                data?.quantity
                                            )}
                                        </td>
                                    </tr>
                                )
                            })
                        ) : (
                            <tr><td colSpan='5' className='text-center'>No line items found</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>



            {/* Submit Button */}
            {canCreateDI && (
                <Row className='mt-4'>
                    <Col md={12} className='text-end'>
                        <button
                            className='btn btn-primary'
                            type='button'
                            onClick={handleCreateDI}
                            disabled={submitting}
                        >
                            {submitting ? 'Creating...' : 'Create Delivery Instruction'}
                        </button>
                    </Col>
                </Row>
            )}
        </CardBody>
    )
}

export default FocCreateDi
